/**
 * Line flows shared by the line and N LINES inspectors (W2-06; DESIGN.md §5.5, §4.21):
 *  - `changeLineType`: the Type dropdown. Image / GIF types need an image (picked from the project's images when a
 *    line has none that fits), UI movers need a HUD element; a change that removes data opens DIALOG.lineType for
 *    one line (W2-07 explains the loss and applies it) or asks once for several lines, then applies everything in
 *    one undoable transaction.
 *  - `changeParent`: the parent picker. One line: DIALOG.parentPrompt (keep numbers / keep place); several lines:
 *    the same choice asked once. Core.lines.setParent keeps the hierarchy cycle-free.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { LineTypeChoice } from '../../core/commands/types';
import { isGifAsset } from '../../core/model/lineKind';
import type { AssetId, Line, LineId, UiElement } from '../../core/model/types';
import type { DocStore } from '../../state/types';
import { setParentOfLines, setTypeOfLines } from './commands';
import { fmtList, LINE_TYPE_LABEL, UI_ELEMENT_LABEL } from './labels';
import { m } from './inspector.messages';
import { imageAssets, isImageAssetRef, lineTypeOf, typeChangeLoss, UI_ELEMENT_LIST, type LossItem } from './model';
import { playheadSec, runTx } from './tx';

const LOSS_TEXT = {
  texture: m.lossTexture,
  text: m.lossText,
  paint: m.lossPaint,
  gif: m.lossGif,
  autoLoop: m.lossAutoLoop,
  attachUI: m.lossAttachUi,
} as const;

/** "the image, the text keyframes" for the confirmation. */
export function lossText(services: AppServices, losses: readonly LossItem[]): string {
  const kinds = [...new Set(losses.map((l) => l.kind))];
  return fmtList(
    services.i18n.locale,
    kinds.map((k) => services.i18n.t(LOSS_TEXT[k])),
    'conjunction',
  );
}

function textureFits(store: DocStore, line: Line, target: 'image' | 'gif'): boolean {
  if (line.texture === null) return false;
  const a = store.doc.assets.get(line.texture);
  if (a === undefined || a.missing === true) return false;
  return target === 'gif' ? isGifAsset(a) : isImageAssetRef(a);
}

/** Changes the type of `ids` to `target` (asks for an image / element and confirms data loss when needed). */
export async function changeLineType(
  services: AppServices,
  store: DocStore,
  ids: readonly LineId[],
  target: LineTypeChoice,
): Promise<boolean> {
  const lines = ids
    .map((id) => store.doc.lines.get(id))
    .filter((l): l is Line => l !== undefined && lineTypeOf(store.doc, store.idx, l) !== target);
  if (lines.length === 0) return false;

  let texture: AssetId | undefined;
  if ((target === 'image' || target === 'gif') && lines.some((l) => !textureFits(store, l, target))) {
    const options = imageAssets(store.doc, target === 'gif');
    if (options.length === 0) {
      services.ui.toast(
        { key: target === 'gif' ? m.noGifs : m.noImages },
        {
          kind: 'warning',
          action: {
            label: { key: m.addImage },
            command: 'assets.add',
            args: { role: target === 'gif' ? 'gif' : 'texture' },
          },
        },
      );
      return false;
    }
    const chosen = await services.ui.choose<string>({
      title: { key: target === 'gif' ? m.pickGif : m.pickImage },
      options: options.map((a) => ({ value: a.id, label: { key: m.assetOption, params: { path: a.path } } })),
    });
    if (chosen === null) return false;
    texture = chosen as AssetId;
  }

  let attachUI: UiElement | undefined;
  if (target === 'uiMover' && lines.some((l) => l.attachUI === null)) {
    const chosen = await services.ui.choose<UiElement>({
      title: { key: m.pickElement },
      options: UI_ELEMENT_LIST.map((e) => ({ value: e, label: { key: UI_ELEMENT_LABEL[e] } })),
    });
    if (chosen === null) return false;
    attachUI = chosen;
  }

  const losses = lines.flatMap((l) => typeChangeLoss(store.idx, l, target));
  if (losses.length > 0) {
    const only = lines[0];
    if (lines.length === 1 && only !== undefined) {
      // W2-07's dialog explains what is removed and runs Core.lines.setLineType itself, with the image or HUD
      // element chosen here pre-selected (LineTypeProps, CR-W2-06-1).
      const r = await services.ui.openDialog(DIALOG.lineType, {
        line: only.id,
        target,
        ...(texture !== undefined ? { texture } : {}),
        ...(attachUI !== undefined ? { attachUI } : {}),
      });
      return r !== null;
    }
    const ok = await services.ui.confirm({
      title: { key: m.typeLossTitle },
      body: {
        key: m.typeLossBody,
        params: {
          count: lines.length,
          type: services.i18n.t(LINE_TYPE_LABEL[target]),
          what: lossText(services, losses),
        },
      },
      confirm: { key: m.typeLossConfirm },
      danger: true,
    });
    if (!ok) return false;
  }
  const out = runTx(services, store, { label: { key: m.txLineType } }, (tx) => {
    setTypeOfLines(tx, { lines: lines.map((l) => l.id), type: target, texture, attachUI });
  });
  return out.ok;
}

/** Sets the parent of `ids` after asking how they should follow it. */
export async function changeParent(
  services: AppServices,
  store: DocStore,
  ids: readonly LineId[],
  parent: LineId | null,
): Promise<boolean> {
  const lines = ids.filter((id) => store.doc.lines.get(id)?.parent !== parent);
  const first = lines[0];
  if (first === undefined) return false;
  let mode: 'keepNumbers' | 'keepPlace' | null;
  if (lines.length === 1) {
    mode = await services.ui.openDialog(DIALOG.parentPrompt, { line: first, parent });
  } else {
    mode = await services.ui.choose<'keepNumbers' | 'keepPlace'>({
      title: { key: m.parentPromptTitle },
      body: { key: m.rotateWithParentSentence },
      options: [
        { value: 'keepNumbers', label: { key: m.parentKeepNumbers }, description: { key: m.parentKeepNumbersDesc } },
        { value: 'keepPlace', label: { key: m.parentKeepPlace }, description: { key: m.parentKeepPlaceDesc } },
      ],
    });
  }
  if (mode === null) return false;
  const chosen = mode;
  const atSec = playheadSec(services);
  const out = runTx(services, store, { label: { key: m.txParent } }, (tx) => {
    setParentOfLines(tx, { lines, parent, mode: chosen, atSec });
  });
  return out.ok;
}
